import {
    ShadowBar,
    ShadowBarChart,
    ShadowBarGroup,
} from '@/components/ShadowBarChart';
import { WithLoader } from '@/components/WithLoader';
import { useOptimizationSession } from '@/dashboards/CampaignOptimization/hooks/useOptimizationSession';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { roundToDecimals, roundToNearest } from '@/utils/numberUtils';
import { Typography } from '@analytical-alley/ui';
import React, { useMemo } from 'react';

export const ContributionShare = () => {
    const { contributionFormatter } = useDashboardGlobalContext();
    const { colorMap } = useDashboardGlobalContext();
    const {
        data: {
            expectedContributionByCategoryData,
            previous: { contributionsByCategory },
            contributionTotalWithNewMedia,
        },
        isDataLoading,
        isDataFetching,
    } = useOptimizationSession();

    const { data, highestValue, lowestValue } = useMemo(() => {
        let highestValue = 0;
        let lowestValue = 0;

        const formattedData: {
            name: string;
            value: number;
            color: string;
            isOptimized: boolean;
        }[] = [];

        Object.entries(
            Object.keys(contributionsByCategory).length
                ? contributionsByCategory
                : expectedContributionByCategoryData,
        ).forEach(([category, contribution]) => {
            const isOptimized =
                expectedContributionByCategoryData[category] != null;
            const value = isOptimized
                ? expectedContributionByCategoryData[category]!
                : contribution;
            highestValue = Math.max(highestValue, Math.abs(value));
            if (value < 0) lowestValue = Math.min(lowestValue, value);

            formattedData.push({
                name: category,
                value: value,
                color: colorMap.categoryColorMap[category]!,
                isOptimized,
            });
        });

        return {
            data: formattedData.sort((a, b) => b.value - a.value),
            highestValue,
            lowestValue,
        };
    }, [
        colorMap.categoryColorMap,
        contributionsByCategory,
        expectedContributionByCategoryData,
    ]);

    const height = 250;
    const scale = roundToNearest(highestValue);
    const minScale = lowestValue < 0 ? roundToNearest(lowestValue) : undefined;

    const barRef = React.useRef();

    return (
        <div className="relative">
            <WithLoader isLoading={isDataLoading} isFetching={isDataFetching}>
                <div className="dark:bg-white/10 rounded-3xl shadow-lg p-6">
                    <div className="flex mb-4">
                        <Typography
                            variant="titleS"
                            tooltip="How much KPI each media category and other factors generate over the campaign period."
                        >
                            Contribution Share
                        </Typography>
                        <span className="grow" />
                    </div>

                    <div className="relative">
                        <div className="flex justify-between">
                            <ShadowBarChart
                                shadowBarRef={barRef}
                                showTickMarks
                                yAxis={'auto'}
                                axisLabelFormatter={contributionFormatter}
                                height={height}
                                maxValue={scale}
                                minValue={minScale}
                                yAxisTitle={'Contribution'}
                                options={{
                                    yAxisLinesStyle: 'solid',
                                    labelVariant: 'rotated',
                                }}
                            >
                                <ShadowBarChart.Content>
                                    {data.map(
                                        (
                                            { value, color, isOptimized, name },
                                            index,
                                        ) => {
                                            return (
                                                <ShadowBarGroup
                                                    activeDotColor={color}
                                                    active={isOptimized}
                                                    key={index}
                                                    title={name}
                                                    tooltipContent={
                                                        <div className="tooltip-content">
                                                            <Typography className="tooltip-header dark:text-dark">
                                                                {name}
                                                            </Typography>
                                                            <table className="font-sans">
                                                                <tbody>
                                                                    <tr>
                                                                        <td className="pr-2">
                                                                            <Typography className="text-black dark:text-black">
                                                                                Contribution
                                                                                amount:
                                                                            </Typography>
                                                                        </td>
                                                                        <td>
                                                                            <Typography className="text-dark dark:text-dark">
                                                                                {contributionFormatter(
                                                                                    value,
                                                                                )}
                                                                            </Typography>
                                                                        </td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="pr-2">
                                                                            <Typography className="text-black dark:text-black">
                                                                                Contribution
                                                                                %:
                                                                            </Typography>
                                                                        </td>
                                                                        <td>
                                                                            <Typography className="text-dark dark:text-dark">
                                                                                {roundToDecimals(
                                                                                    Math.max(
                                                                                        (value /
                                                                                            contributionTotalWithNewMedia) *
                                                                                            100,
                                                                                        0,
                                                                                    ),
                                                                                    2,
                                                                                )}
                                                                                %
                                                                            </Typography>
                                                                        </td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    }
                                                >
                                                    <ShadowBar
                                                        ref={barRef}
                                                        height={value}
                                                        backgroundColor={
                                                            color || '#2B67FF'
                                                        }
                                                    >
                                                        <ShadowBar.Title className="text-dark dark:text-white">
                                                            {contributionFormatter(
                                                                value,
                                                            )}
                                                        </ShadowBar.Title>
                                                    </ShadowBar>
                                                </ShadowBarGroup>
                                            );
                                        },
                                    )}
                                </ShadowBarChart.Content>
                            </ShadowBarChart>
                        </div>
                    </div>
                </div>
            </WithLoader>
        </div>
    );
};
